import React from 'react';
import { Award, Users, Globe, Star, Target, Trophy } from 'lucide-react';
import { mockData } from '../mock';

const About = () => {
  const achievements = [
    { icon: Trophy, title: "25+ Years", description: "Industry Experience" },
    { icon: Users, title: "100+ Professionals", description: "Trained & Certified" },
    { icon: Globe, title: "Pan India", description: "Nationwide Operations" },
    { icon: Award, title: "Authorized Trainer", description: "Rupes Italy Partner" }
  ];

  const values = [
    {
      icon: Target,
      title: "Excellence",
      description: "We strive for perfection in every service and training program we deliver."
    },
    {
      icon: Star,
      title: "Innovation",
      description: "Constantly adopting latest technologies and techniques in automotive care."
    },
    {
      icon: Users,
      title: "Empowerment",
      description: "Empowering professionals with skills and knowledge to build successful businesses."
    },
    {
      icon: Globe,
      title: "Global Standards",
      description: "Maintaining international quality standards in all our services and training."
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900">
              About
              <span className="text-blue-600"> Nirmal Narayan</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Automotive Expert, Entrepreneur, and Industry Leader with 25+ Years of Excellence
            </p>
          </div>
        </div>
      </section>

      {/* Main Bio Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <h2 className="text-4xl font-bold text-gray-900">
                  The Journey of
                  <span className="text-blue-600"> Excellence</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {mockData.founder.bio}
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Nirmal's passion for automotive excellence began over two decades ago. His deep understanding 
                  of paint protection, detailing techniques, and business development has made him a sought-after 
                  expert in the industry. Through Unisol Global, he continues to set new standards in automotive 
                  care while empowering the next generation of professionals.
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-gray-900">Specializations</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {mockData.founder.specializations.map((spec, index) => (
                    <div key={index} className="flex items-center space-x-3 bg-blue-50 p-4 rounded-lg border border-blue-100">
                      <Star className="text-blue-600 flex-shrink-0" size={20} />
                      <span className="text-gray-700 text-sm">{spec}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200">
                <div className="space-y-8">
                  <div className="text-center">
                    <div className="w-32 h-32 rounded-full mx-auto mb-6 overflow-hidden border-4 border-blue-600">
                      <img 
                        src="https://images.unsplash.com/photo-1672844825476-66737d85bfce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxhdXRvbW90aXZlJTIwd29ya3Nob3B8ZW58MHx8fHwxNzU4MjE5MTgxfDA&ixlib=rb-4.1.0&q=85" 
                        alt="Nirmal Narayan - Automotive Expert"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{mockData.founder.name}</h3>
                    <p className="text-blue-600 font-semibold">{mockData.founder.title}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600">{mockData.founder.experience}</div>
                      <div className="text-gray-500 text-sm">Experience</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600">{mockData.founder.studentsTrained}</div>
                      <div className="text-gray-500 text-sm">Students Trained</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold text-gray-900">
              Key
              <span className="text-blue-600"> Achievements</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Milestones that define our journey of excellence in the automotive industry
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => {
              const IconComponent = achievement.icon;
              return (
                <div key={index} className="text-center space-y-4 bg-white p-8 rounded-2xl border border-gray-200 hover:border-blue-300 transition-all duration-300 hover:-translate-y-2">
                  <div className="w-16 h-16 bg-blue-600 rounded-full mx-auto flex items-center justify-center">
                    <IconComponent className="text-white" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold text-blue-600">{achievement.title}</h3>
                  <p className="text-gray-600 text-sm">{achievement.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold text-gray-900">
              Our
              <span className="text-blue-600"> Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do at Unisol Global
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} className="bg-gray-50 rounded-xl p-8 border border-gray-200 hover:border-blue-300 transition-all duration-300">
                  <div className="flex items-start space-x-6">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComponent className="text-white" size={24} />
                    </div>
                    <div className="space-y-3">
                      <h3 className="text-xl font-bold text-gray-900">{value.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold text-gray-900">
              Company
              <span className="text-blue-600"> Journey</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From humble beginnings to industry leadership
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              <div className="flex items-center space-x-8 bg-white rounded-xl p-6 border border-gray-200">
                <div className="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">1999</span>
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-gray-900">The Beginning</h3>
                  <p className="text-gray-600">Started automotive journey with passion for excellence and innovation</p>
                </div>
              </div>

              <div className="flex items-center space-x-8 bg-white rounded-xl p-6 border border-gray-200">
                <div className="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">2010</span>
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-gray-900">Unisol Global Founded</h3>
                  <p className="text-gray-600">Established company focusing on premium automotive detailing services</p>
                </div>
              </div>

              <div className="flex items-center space-x-8 bg-white rounded-xl p-6 border border-gray-200">
                <div className="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">2018</span>
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-gray-900">Training Center Launch</h3>
                  <p className="text-gray-600">Opened professional training facility to certify the next generation of detailers</p>
                </div>
              </div>

              <div className="flex items-center space-x-8 bg-white rounded-xl p-6 border border-gray-200">
                <div className="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">2024</span>
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-gray-900">Nationwide Expansion</h3>
                  <p className="text-gray-600">Expanded training programs across India, serving automotive professionals nationwide</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;